// OPS operational data hooks — polling hooks for all /api/ops/* endpoints.
// Default interval is 120s (non-critical, infrequently updated operational data).
//
// ── Field-naming convention ──────────────────────────────────────────
// The worker (`src/worker/ops-api.ts`) normalizes every result row:
//   1. Column names are lowercased + snake_cased (e.g. `Date_Start` → `date_start`).
//   2. A handful of handlers add explicit aliases when the upstream column name
//      doesn't match frontend expectations (e.g. `name` → `unit_number`, `products`
//      → `item_name`). Where applicable, the row interfaces below list both the
//      canonical name AND the known alternate(s) so callers can read via
//      `row.canonical ?? row.alternate` without reaching for `as Record<...>`.
// Every interface has a `[key: string]: unknown` index signature because column
// sets evolve dynamically with the upstream Airtable schema.

import { usePolling } from "./useDashboard";

// ── Shared wrapper ────────────────────────────────────────────────────────────

/**
 * All OPS API endpoints return the rows plus a completeness envelope.
 *
 * `count` is the number of rows in THIS response — it always was, but for a
 * long time it was the ONLY number on the wire, which made a 500-row page of an
 * 880-row table indistinguishable from the whole table. `total` is the row
 * count over the same filter with no LIMIT/OFFSET, and `truncated` /
 * `completeness` say plainly when rows are missing.
 *
 * The envelope fields are optional so a cached or older payload still
 * typechecks; `rowWindowState()` in `lib/rowWindow.ts` resolves their ABSENCE
 * to "unknown", never to "complete". Read the envelope through that helper —
 * do not branch on `truncated` by hand.
 */
export interface OpsResponse<T> {
  data: T[];
  count: number;
  /** Rows matching the filter with no page cap; null when it could not be read. */
  total?: number | null;
  truncated?: boolean;
  completeness?: "complete" | "truncated" | "unknown";
  limit?: number;
  offset?: number;
}

// ── Page limits ───────────────────────────────────────────────────────────────
//
// Every hook passes an EXPLICIT limit. The API's default is 500 and its hard
// maximum is 5,000 (`parseIntParam` in `src/worker/ops-api.ts` clamps there).
// Nine hooks used to pass nothing at all, so the 500 default silently decided
// what the department could see — dash_ems_stock_levels (880 rows) rendered
// with 380 rows missing and no way for the page to know.
//
// Limits below are sized for roughly 4x or better headroom against the live
// remote-D1 row counts verified 2026-08-27, and are commented with the count
// they were sized against so the next person can re-check rather than guess.
// Three tables cannot be given headroom because they are at or past the hard
// cap (gear 4,100 · uniforms 4,179 · IRONS 24,896). The cap is deliberately NOT
// raised for them: the honest outcome is the loud truncation notice the
// endpoint now returns, not an unbounded query.

// ── Row interfaces ────────────────────────────────────────────────────────────

/** dash_gear_inventory — PPE gear inventory records. Worker sorts by `asset_type`. */
export interface GearInventoryRow {
  id?: string | null;
  airtable_id?: string | null;
  member?: string | null;
  station?: string | null;
  asset_type?: string | null;
  gear_type?: string | null;
  brand?: string | null;
  model?: string | null;
  size?: string | null;
  serial_number?: string | null;
  issued_date?: string | null;
  retirement_date?: string | null;
  status?: string | null;
  condition?: string | null;
  notes?: string | null;
  synced_at?: string | null;
  [key: string]: unknown;
}

/** dash_uniforms — uniform inventory records. Worker sorts by `asset_type`. */
export interface UniformRow {
  id?: string | null;
  airtable_id?: string | null;
  member?: string | null;
  station?: string | null;
  asset_type?: string | null;
  uniform_type?: string | null;
  size?: string | null;
  quantity?: number | null;
  issued_date?: string | null;
  status?: string | null;
  notes?: string | null;
  synced_at?: string | null;
  [key: string]: unknown;
}

/**
 * dash_scba_cylinders — SCBA cylinder records. Worker sorts by `end_of_life`.
 *
 * Fields below are the LIVE columns (verified against remote D1 2026-08-27),
 * in the worker's toSnakeCase() form. `nex_test_due` is not a typo in this
 * file — the live column really is `Nex_Test_Due`, with one 't'.
 *
 * This interface previously also declared `hydro_expiry`, `retire_date`,
 * `station`, `apparatus`, `hydro_date` and `capacity`. NONE of those columns
 * exist. Declaring them as optional made `row.hydro_expiry` typecheck as
 * `string | null | undefined` while being permanently `undefined` at runtime,
 * which is how the SCBA compliance tile reported 100% current for 570
 * cylinders. They are removed deliberately: with only the index signature
 * left, reading a non-existent field yields `unknown` and FAILS to compile
 * when passed to a date parser. Do not re-add a field this table lacks.
 */
export interface ScbaCylinderRow {
  id?: string | null;
  airtable_id?: string | null;
  airtable_created_time?: string | null;
  cylinder_number?: string | null;
  cylinder_type?: string | null;
  manufacture_date?: string | null;
  end_of_life?: string | null;
  test_dates?: string | null;
  nex_test_due?: string | null;
  synced_at?: string | null;
  [key: string]: unknown;
}

/**
 * dash_scba_g1_cylinders — SCBA G1 cylinder records. Worker sorts by
 * `hydro_due_date`. LIVE columns only (verified against remote D1 2026-08-27).
 *
 * Note this table has NO end-of-life column, so a G1 cylinder can only be
 * classified in or out of hydro test — never end-of-life. Phantom fields
 * removed for the same reason as ScbaCylinderRow above.
 */
export interface ScbaG1CylinderRow {
  id?: string | null;
  airtable_id?: string | null;
  airtable_created_time?: string | null;
  serial_number?: string | null;
  category?: string | null;
  manufacture_date?: string | null;
  hydro_due_date?: string | null;
  status?: string | null;
  hydro_test_date?: string | null;
  synced_at?: string | null;
  [key: string]: unknown;
}

/**
 * ── CLOSED SHAPES ────────────────────────────────────────────────────────────
 *
 * The five row types below are CLOSED — no `[key: string]: unknown` — and list
 * exactly the keys the handler puts on the wire: the live D1 columns
 * snake_cased by `normalizeRow` in `ops-api.ts`, plus that handler's explicit
 * aliases. Observed by running the REAL handlers over the REAL live DDL
 * (sqlite_master on remote D1, 2026-08-27) in `EquipmentGear.wire.test.ts` and
 * `CommunityEngagement.wire.test.ts` — not copied from an assumption.
 *
 * WHY CLOSED. An index signature types `row.anything` as `unknown`, and every
 * reader in this codebase accepts `unknown`. Reading a column that exists
 * NOWHERE therefore compiles clean and returns `undefined`. Removing a phantom
 * field declaration is NOT sufficient on its own: with the index signature
 * still present the access stays typed `unknown` and `tsc` stays green. Only
 * closing the shape turns the read into TS2339.
 *
 * What that hid, live, until 2026-08-27: all 326 respirator fit-test records
 * classified `no_record` (page read `member`/`test_date`/`expiry_date`; the
 * wire carries `members`/`member`, `last_test_date`, `test_date_due`), and the
 * non-compliant table excludes `no_record` — so a fleet where 163/163 G1 and
 * 163/163 Advantage tests were PAST DUE rendered "0 expired".
 */

/**
 * dash_fit_test_g1 — G1 SCBA fit test records. Worker sorts by `test_date_due`.
 *
 * NOTE `members` (PLURAL) — the G1 table spells it that way and the Advantage
 * table spells it `member`. Reading `member` off a G1 row is the defect that
 * dropped all 163 G1 records out of Assignment's fit-tested set.
 */
export interface FitTestG1Row {
  airtable_id?: string | null;
  airtable_created_time?: string | null;
  dept_id?: string | null;
  id?: string | null;
  id1?: number | null;
  inactive_status_date?: string | null;
  last_test_date?: string | null;
  mask_size?: string | null;
  members?: string | null;
  status?: string | null;
  synced_at?: string | null;
  test_date_due?: string | null;
}

/** dash_fit_test_advantage — Advantage respirator fit test records. Worker sorts by `test_date_due`. */
export interface FitTestAdvantageRow {
  active_status?: string | null;
  airtable_id?: string | null;
  airtable_created_time?: string | null;
  dept_id?: string | null;
  id?: string | null;
  id1?: number | null;
  last_test_date?: string | null;
  mask_size?: string | null;
  /** SINGULAR here; the G1 table's equivalent column is `members`. */
  member?: string | null;
  status?: string | null;
  synced_at?: string | null;
  test_date_due?: string | null;
}

/**
 * dash_disposals — equipment disposal records. Worker sorts by `date_of_disposal`.
 *
 * The date column is `date_of_disposal`. There is no `disposal_date`; reading
 * it returned undefined for all 462 rows and emptied the disposal trend chart.
 */
export interface DisposalRow {
  airtable_id?: string | null;
  airtable_created_time?: string | null;
  approx_value?: number | null;
  date_of_disposal?: string | null;
  idnum?: number | null;
  item_being_disposed?: string | null;
  method_of_disposal?: string | null;
  name?: string | null;
  notes?: string | null;
  serial_number_if_applicable?: string | null;
  synced_at?: string | null;
}

/**
 * dash_gear_collection — gear collection / turn-in records. Worker sorts by `created_on`.
 *
 * The member is `name`, the date is `created_on`, and what is owed is
 * `items_to_return`. There is no station, quantity, gear_type or collected_by
 * column on this table at all — do not re-add them without checking the wire.
 */
export interface GearCollectionRow {
  airtable_id?: string | null;
  airtable_created_time?: string | null;
  comments?: string | null;
  created_on?: string | null;
  email?: string | null;
  id?: string | null;
  idnum?: number | null;
  items_to_return?: string | null;
  name?: string | null;
  phone?: string | null;
  status?: string | null;
  synced_at?: string | null;
}

/**
 * dash_irons_issues — IRONS issue tracking records.
 *
 * Worker aliases: `issue_status → status`, `name → subject` (when subject null).
 */
export interface IronRow {
  id?: string | null;
  airtable_id?: string | null;
  subject?: string | null;
  name?: string | null;
  category?: string | null;
  status?: string | null;
  issue_status?: string | null;
  priority?: string | null;
  station?: string | null;
  unit?: string | null;
  created_on?: string | null;
  resolution_date?: string | null;
  notes?: string | null;
  synced_at?: string | null;
  [key: string]: unknown;
}

/**
 * dash_fleet — apparatus/vehicle fleet records.
 *
 * Worker `ops-api.ts.handleGetFleet` normalizes DB casing (Unit → unit,
 * Vehicle_Type → vehicle_type) and then aliases `unit → unit_number` and
 * `vehicle_type → apparatus_type` so both names resolve. Consumers should
 * use `unit ?? unit_number` and `vehicle_type ?? apparatus_type`.
 */
export interface FleetRow {
  id?: string | null;
  airtable_id?: string | null;
  unit?: string | null;
  unit_number?: string | null;
  vehicle_type?: string | null;
  apparatus_type?: string | null;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  vin?: string | null;
  station?: string | null;
  status?: string | null;
  mileage?: number | null;
  last_inspection_date?: string | null;
  next_inspection_date?: string | null;
  notes?: string | null;
  synced_at?: string | null;
  [key: string]: unknown;
}

/**
 * dash_fleet_replacement — fleet replacement planning records.
 *
 * Worker aliases `name → unit_number` and `apparatus → apparatus_type`.
 * Columns are appended dynamically by the Airtable sync; the stable
 * subset the frontend relies on is listed here.
 */
export interface FleetReplacementRow {
  id?: string | null;
  airtable_id?: string | null;
  name?: string | null;
  unit_number?: string | null;
  apparatus?: string | null;
  apparatus_type?: string | null;
  vehicle_type_from_apparatus?: string | null;
  years_in_service?: number | null;
  max_age?: number | null;
  age_difference?: number | null;
  beyond_repair?: boolean | number | null;
  priority?: string | null;
  status?: string | null;
  notes?: string | null;
  synced_at?: string | null;
  [key: string]: unknown;
}

/**
 * dash_ems_stock_levels — EMS supply stock records by station.
 *
 * Worker aliases: `products → item_name`, `current_stock → quantity_on_hand`,
 * `stock_max → reorder_level`. Order by `Station`, `Products`.
 */
export interface EmsStockRow {
  id?: string | null;
  airtable_id?: string | null;
  station?: string | null;
  item_name?: string | null;
  products?: string | null;
  category?: string | null;
  quantity_on_hand?: number | null;
  current_stock?: number | null;
  reorder_level?: number | null;
  stock_max?: number | null;
  unit?: string | null;
  expiry_date?: string | null;
  last_checked_date?: string | null;
  notes?: string | null;
  synced_at?: string | null;
  [key: string]: unknown;
}

/**
 * dash_ems_warehouse — EMS central warehouse inventory records.
 *
 * Worker aliases: `product → item_name`, `warehouse_stock_number|warehouse_stock
 * → quantity_on_hand`. Order by `Product`.
 */
export interface EmsWarehouseRow {
  id?: string | null;
  airtable_id?: string | null;
  item_name?: string | null;
  product?: string | null;
  category?: string | null;
  quantity_on_hand?: number | null;
  warehouse_stock?: number | null;
  warehouse_stock_number?: number | null;
  reorder_level?: number | null;
  unit?: string | null;
  expiry_date?: string | null;
  last_checked_date?: string | null;
  notes?: string | null;
  synced_at?: string | null;
  [key: string]: unknown;
}

/**
 * dash_community_events — community outreach and event records.
 *
 * CLOSED. The event name is `title`, the type is `type_of_event` and the head
 * count is `number_of_attendees`. The page read `event_name`/`event_type`/
 * `attendance` — none of which exist — so every one of 92 events rendered a
 * blank name and a blank attendance, and the type-distribution chart bucketed
 * all 92 into "Other".
 */
export interface CommunityEventRow {
  airtable_id?: string | null;
  airtable_created_time?: string | null;
  contact?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  date_end?: string | null;
  date_start?: string | null;
  dcvfd_team?: string | null;
  id?: string | null;
  idnum?: number | null;
  location?: string | null;
  number_of_attendees?: number | null;
  organization?: string | null;
  station?: string | null;
  status?: string | null;
  synced_at?: string | null;
  title?: string | null;
  type_of_audience?: string | null;
  type_of_event?: string | null;
}

/**
 * dash_roster — crew assignment records. Worker sorts by `Assignment`.
 *
 * ── CLOSED, AND DERIVED FROM THE LIVE TABLE ─────────────────────────────────
 *
 * This interface used to declare `id`, `co_id`, `station`, `rank`, `status`,
 * `assigned_date`, `certifications` and `notes` — none of which exist — behind
 * a `[key: string]: unknown` index signature that made every one of them
 * typecheck. `Assignment.tsx` read `row.station` off it, compiled clean, and
 * bucketed all 305 live members into "Other".
 *
 * The keys below are the live columns (remote D1, `pragma_table_info`,
 * 2026-08-27) after the worker's `normalizeRow()` snake-cases them:
 *
 *   airtable_id · synced_at · airtable_created_time · assignment · battalion ·
 *   role · member · unit · location_from_unit · status_new_from_member ·
 *   vehicle_type_from_unit · headshot_from_member
 *
 * The index signature is GONE. A field that is not here is a compile error, on
 * purpose. Note that being on the wire is not the same as being readable —
 * several of these are unresolved Airtable link fields (see below).
 */
export interface RosterRow {
  airtable_id?: string | null;
  synced_at?: string | null;
  airtable_created_time?: string | null;
  /**
   * Free text, e.g. `"Eric J. Escalante Duarte (102499) Battalion 3 - E513
   * (DC-24) - LFF"`. 172 distinct values over 305 rows. Battalion + unit, not
   * a station. This is also what `?station=` on `/api/ops/roster` filters on.
   */
  assignment?: string | null;
  /** UNRESOLVED Airtable record ID (`recX0BqFAExVPvXTc`), 5 distinct. Not a name. */
  battalion?: string | null;
  /** UNRESOLVED Airtable record ID. Not a rank name. */
  role?: string | null;
  member?: string | null;
  /** UNRESOLVED Airtable record ID, 10 distinct. Not a unit designator. */
  unit?: string | null;
  /**
   * UNRESOLVED Airtable record ID and 179/305 NULL. The name says "location";
   * the values are `recYOVRz1ZldJDlRL` and two others. Repointing a station
   * chart at this would print record IDs as station names.
   */
  location_from_unit?: string | null;
  status_new_from_member?: string | null;
  vehicle_type_from_unit?: string | null;
  headshot_from_member?: string | null;
}

// ── useGearInventory ──────────────────────────────────────────────────────────

/**
 * Fetches GET /api/ops/gear?limit=5000 and polls every 2 min.
 * Returns PPE gear inventory records.
 *
 * Limit 5,000 = the API hard maximum. Live: 4,100 rows (2026-08-27) — only 1.2x
 * headroom and NOT raisable. This endpoint will start reporting truncated when
 * the table passes 5,000; the page must keep rendering TruncationNotice.
 */
export function useGearInventory(intervalMs = 120_000) {
  return usePolling<OpsResponse<GearInventoryRow>>(
    "/api/ops/gear?limit=5000",
    intervalMs,
  );
}

// ── useUniforms ───────────────────────────────────────────────────────────────

/**
 * Fetches GET /api/ops/uniforms?limit=5000 and polls every 2 min.
 * Returns uniform inventory records.
 *
 * Limit 5,000 = the API hard maximum. Live: 4,179 rows (2026-08-27) — 1.2x
 * headroom, not raisable. Closest table to the cap after IRONS.
 */
export function useUniforms(intervalMs = 120_000) {
  return usePolling<OpsResponse<UniformRow>>(
    "/api/ops/uniforms?limit=5000",
    intervalMs,
  );
}

// ── useScbaCylinders ──────────────────────────────────────────────────────────

/**
 * Fetches GET /api/ops/scba-cylinders?limit=2000 and polls every 2 min.
 * Returns SCBA cylinder inventory and hydro status records.
 *
 * Limit 2,000 against 352 live rows (2026-08-27) — 5.7x headroom. This hook
 * passed no limit and rode the 500 default; the table would have started
 * dropping cylinders from a compliance page at 501.
 */
export function useScbaCylinders(intervalMs = 120_000) {
  return usePolling<OpsResponse<ScbaCylinderRow>>(
    "/api/ops/scba-cylinders?limit=2000",
    intervalMs,
  );
}

// ── useScbaG1Cylinders ────────────────────────────────────────────────────────

/**
 * Fetches GET /api/ops/scba-g1-cylinders?limit=2000 and polls every 2 min.
 * Returns SCBA G1 cylinder inventory and hydro status records.
 *
 * Limit 2,000 against 218 live rows (2026-08-27) — 9.2x headroom. Sized to
 * match the non-G1 cylinder hook so the two halves of the same compliance tile
 * cannot be capped differently.
 */
export function useScbaG1Cylinders(intervalMs = 120_000) {
  return usePolling<OpsResponse<ScbaG1CylinderRow>>(
    "/api/ops/scba-g1-cylinders?limit=2000",
    intervalMs,
  );
}

// ── useFitTestsG1 ─────────────────────────────────────────────────────────────

/**
 * Fetches GET /api/ops/fit-tests-g1?limit=1000 and polls every 2 min.
 * Returns G1 SCBA respirator fit test records.
 *
 * Limit 1,000 against 163 live rows (2026-08-27) — 6.1x headroom. One row per
 * member per test cycle, so it tracks roster size (305) rather than growing
 * without bound.
 */
export function useFitTestsG1(intervalMs = 120_000) {
  return usePolling<OpsResponse<FitTestG1Row>>(
    "/api/ops/fit-tests-g1?limit=1000",
    intervalMs,
  );
}

// ── useFitTestsAdvantage ──────────────────────────────────────────────────────

/**
 * Fetches GET /api/ops/fit-tests-advantage?limit=1000 and polls every 2 min.
 * Returns Advantage respirator fit test records.
 *
 * Limit 1,000 against 163 live rows (2026-08-27) — 6.1x headroom. Matched to
 * the G1 fit-test hook for the same reason.
 */
export function useFitTestsAdvantage(intervalMs = 120_000) {
  return usePolling<OpsResponse<FitTestAdvantageRow>>(
    "/api/ops/fit-tests-advantage?limit=1000",
    intervalMs,
  );
}

// ── useDisposals ──────────────────────────────────────────────────────────────

/**
 * Fetches GET /api/ops/disposals?limit=3000 and polls every 2 min.
 * Returns equipment disposal records.
 *
 * Raised 1,000 → 3,000: 462 live rows (2026-08-27) left only 2.2x headroom on a
 * table that only ever grows (a disposal is never un-disposed). 6.5x now.
 */
export function useDisposals(intervalMs = 120_000) {
  return usePolling<OpsResponse<DisposalRow>>(
    "/api/ops/disposals?limit=3000",
    intervalMs,
  );
}

// ── useGearCollection ─────────────────────────────────────────────────────────

/**
 * Fetches GET /api/ops/gear-collection?limit=1000 and polls every 2 min.
 * Returns gear collection / turn-in records.
 *
 * Limit 1,000 against 51 live rows (2026-08-27) — 19.6x headroom. Append-only
 * (one row per turn-in), so headroom is deliberately generous.
 */
export function useGearCollection(intervalMs = 120_000) {
  return usePolling<OpsResponse<GearCollectionRow>>(
    "/api/ops/gear-collection?limit=1000",
    intervalMs,
  );
}

// ── useIrons ──────────────────────────────────────────────────────────────────

interface IronsParams {
  status?: string;
  station?: string;
}

/**
 * Fetches GET /api/ops/irons?limit=5000 with optional ?status and ?station filters.
 * Polls every 2 min. Returns power tool and hydraulic rescue tool records.
 *
 * Limit 5,000 = the API hard maximum, and it is NOT enough: 24,896 live rows
 * (2026-08-27) means an unfiltered read is missing ~80% of the table. The cap
 * is deliberately not raised — an unbounded SELECT over 25k rows is not the
 * honest fix. The endpoint reports truncated:true and IronsIssues renders the
 * notice; narrowing with ?status / ?station is what actually makes a page
 * whole. Server-side aggregation is the real repair and is out of scope here.
 */
export function useIrons(params?: IronsParams, intervalMs = 120_000) {
  const qs = new URLSearchParams();
  qs.set("limit", "5000");
  if (params?.status) qs.set("status", params.status);
  if (params?.station) qs.set("station", params.station);

  return usePolling<OpsResponse<IronRow>>(
    `/api/ops/irons?${qs.toString()}`,
    intervalMs,
  );
}

// ── useFleet ──────────────────────────────────────────────────────────────────

/**
 * Fetches GET /api/ops/fleet?limit=1000 and polls every 2 min.
 * Returns apparatus and vehicle fleet records.
 *
 * Limit 1,000 against 63 live rows (2026-08-27) — 15.9x headroom. The fleet is
 * a fixed asset list; it will not approach this.
 */
export function useFleet(intervalMs = 120_000) {
  return usePolling<OpsResponse<FleetRow>>(
    "/api/ops/fleet?limit=1000",
    intervalMs,
  );
}

// ── useFleetReplacement ───────────────────────────────────────────────────────

/**
 * Fetches GET /api/ops/fleet-replacement?limit=1000 and polls every 2 min.
 * Returns apparatus replacement planning and cost projection records.
 *
 * Limit 1,000 against 45 live rows (2026-08-27) — 22x headroom. One row per
 * apparatus in the replacement plan.
 */
export function useFleetReplacement(intervalMs = 120_000) {
  return usePolling<OpsResponse<FleetReplacementRow>>(
    "/api/ops/fleet-replacement?limit=1000",
    intervalMs,
  );
}

// ── useEmsStock ───────────────────────────────────────────────────────────────

interface EmsStockParams {
  station?: string;
}

/**
 * Fetches GET /api/ops/ems-stock?limit=5000 with optional ?station filter and
 * polls every 2 min. Returns EMS supply stock levels by station.
 *
 * THIS is the hook that was live-wrong: it passed no limit, took the 500
 * default, and rendered EmsSupply over 500 of 880 rows (2026-08-27) — 380 rows,
 * 43% of the department's EMS stock, invisible with nothing on screen saying
 * so. Limit 5,000 = the API hard maximum, giving 5.7x headroom. The table is
 * stations x products, so it grows in blocks when a product line is added; the
 * maximum is the right choice and the truncation notice covers the day it is
 * no longer enough.
 */
export function useEmsStock(params?: EmsStockParams, intervalMs = 120_000) {
  const qs = new URLSearchParams();
  qs.set("limit", "5000");
  if (params?.station) qs.set("station", params.station);
  const url = `/api/ops/ems-stock?${qs.toString()}`;

  return usePolling<OpsResponse<EmsStockRow>>(url, intervalMs);
}

// ── useEmsWarehouse ───────────────────────────────────────────────────────────

/**
 * Fetches GET /api/ops/ems-warehouse?limit=2000 and polls every 2 min.
 * Returns EMS central warehouse inventory records.
 *
 * Limit 2,000 against 288 live rows (2026-08-27) — 6.9x headroom. One row per
 * product in the central warehouse; it grows with the catalog, not with usage.
 */
export function useEmsWarehouse(intervalMs = 120_000) {
  return usePolling<OpsResponse<EmsWarehouseRow>>(
    "/api/ops/ems-warehouse?limit=2000",
    intervalMs,
  );
}

// ── useCommunityEvents ────────────────────────────────────────────────────────

/**
 * Fetches GET /api/ops/community-events?limit=1000 and polls every 2 min.
 * Returns community outreach and event records.
 *
 * Raised 500 → 1,000: 92 live rows (2026-08-27), but the table is append-only
 * and 500 is the same number the broken default used — an explicit limit that
 * happens to equal the default is easy to mistake for "no limit set".
 */
export function useCommunityEvents(intervalMs = 120_000) {
  return usePolling<OpsResponse<CommunityEventRow>>(
    "/api/ops/community-events?limit=1000",
    intervalMs,
  );
}

// ── useRoster ─────────────────────────────────────────────────────────────────

interface RosterParams {
  station?: string;
}

/**
 * Fetches GET /api/ops/roster?limit=2000 with optional ?station filter and polls
 * every 2 min. Returns member station roster records.
 *
 * Limit 2,000 against 305 live rows (2026-08-27) — 6.6x headroom. This hook was
 * also riding the 500 default: at 61% of it, the department was about 195
 * members from silently truncating its own roster.
 */
export function useRoster(params?: RosterParams, intervalMs = 120_000) {
  const qs = new URLSearchParams();
  qs.set("limit", "2000");
  if (params?.station) qs.set("station", params.station);
  const url = `/api/ops/roster?${qs.toString()}`;

  return usePolling<OpsResponse<RosterRow>>(url, intervalMs);
}

// ── useStaffingCapacity ───────────────────────────────────────────────────────

/** A single unit's staffing status within a battalion */
export interface UnitStaffing {
  unit: string;
  type: string;
  minStaff: number;
  maxStaff: number;
  assigned: number;
  status: "covered" | "minimum" | "uncovered";
}

/** Station staffing for one battalion */
export interface StationStaffing {
  station: string;
  units: UnitStaffing[];
}

/** Battalion staffing summary */
export interface BattalionStaffing {
  id: string;
  name: string;
  members: number;
  stations: StationStaffing[];
}

/** A coverage gap entry (unit × battalion below minimum) */
export interface StaffingGap {
  station: string;
  unit: string;
  type: string;
  battalion: string;
  assigned: number;
  minStaff: number;
  severity: "zero" | "below_min";
}

/** Per-station totals across all battalions */
export interface StationTotal {
  station: string;
  assigned: number;
  needed: number;
}

/** Full response from GET /api/ops/staffing-capacity */
export interface StaffingCapacityResponse {
  targets: {
    minimum: number;
    withBuffer: number;
    currentRunning: number;
  };
  /** Roster rows whose member status didn't sync — currentRunning undercounts by up to this. */
  rosterMissingStatus?: number;
  rosterTotal?: number;
  minPerBattalion: number;
  battalions: BattalionStaffing[];
  matrix: Record<string, Record<string, { assigned: number; status: string }>>;
  gaps: StaffingGap[];
  stationTotals: StationTotal[];
  recommendations: string[];
  computedAt: string;
}

/**
 * Fetches GET /api/ops/staffing-capacity and polls every 5 min.
 * Returns the battalion × station × unit staffing capacity matrix.
 */
export function useStaffingCapacity(intervalMs = 300_000) {
  return usePolling<StaffingCapacityResponse>(
    "/api/ops/staffing-capacity",
    intervalMs,
  );
}
